import React, {
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import styled from 'styled-components';
import {
    CloudinaryContext,
    Image,
    Transformation,
    Video
} from 'cloudinary-react';
import cloudinary, {
    Transformation as TransformationType
} from 'cloudinary-core';
import { isUrl } from '../../utils/shared.utils';
import HBGifAsVideo from './HBGifAsVideo';
import { UnstyledButton } from '../../styles/mixins';

export const CLOUDINARY_VIDEO_BASE_URL =
    'https://res.cloudinary.com/honeybook/video/upload';

export const getCloudinaryPathFromCloudinaryUrl = (url: string) => {
    const CLOUDINARY_URL_REGEX =
        /^(https?:\/\/res.cloudinary.com\/honeybook\/image\/upload\/v[0-9]+\/)(.*)$/;
    // this regex should also match cloudinary urls with transformations
    const CLOUDINARY_URL_WITH_TRANSFORMATIONS_REGEX =
        /^(https?:\/\/res.cloudinary.com\/honeybook\/image\/upload\/w_[0-9]+,h_[0-9]+\/v[0-9]+\/)(.*)$/;

    const [, , cloudinaryPath] = url.split(CLOUDINARY_URL_REGEX) || [];

    const [, , cloudinaryWithTransformPath] =
        url.split(CLOUDINARY_URL_WITH_TRANSFORMATIONS_REGEX) || [];
    return cloudinaryPath || cloudinaryWithTransformPath;
};

export function addDefaultFlagsToCloudinaryUrl(
    url: string,
    transformations: TransformationType.Options[] = [
        { fetchFormat: 'auto', flags: 'lossy' }
    ]
) {
    if (!url) {
        return null;
    }
    const cloudinaryPath = getCloudinaryPathFromCloudinaryUrl(url);
    // @ts-ignore
    const cloudinaryCore = new cloudinary.Cloudinary({
        cloud_name: 'honeybook'
    });

    return cloudinaryCore.url(cloudinaryPath, {
        transformation: transformations
    });
}

export function generateCroppedUrl(
    url: string,
    { width, height, x, y, crop },
    flags = {}
) {
    let cloudinaryPath = url;
    if (isUrl(cloudinaryPath)) {
        // For crop transformations to work, path cannot be absolute
        cloudinaryPath = getCloudinaryPathFromCloudinaryUrl(url);
    }

    // @ts-ignore
    const cloudinaryCore = new cloudinary.Cloudinary({
        cloud_name: 'honeybook'
    });

    return cloudinaryCore.url(cloudinaryPath, {
        width,
        height,
        x,
        y,
        crop,
        ...flags
    });
}

interface IGenerateUrlProps {
    path: string;
    transformations?: TransformationType.Options[];
}

// this is a more generic version of url generation
// when we refactor this component and upgrade to @cloudinary-core we'll can
// define wrapper functions for this with common uses
export function generateUrl({ path, transformations }: IGenerateUrlProps) {
    // @ts-ignore
    const cloudinaryCore = new cloudinary.Cloudinary({
        cloud_name: 'honeybook'
    });

    return cloudinaryCore.url(path, { transformation: transformations });
}

export interface IMediaElementFlags {
    quality?: string;
    flags?: string;
    fetchFormat?: string;
}

export type TMediaElementFlags = 'quality' | 'flags' | 'fetchFormat';
export const CROP_TYPE = [
    'fill',
    'lfill',
    'fill_pad',
    'crop',
    'thumb',
    'scale',
    'limit',
    'fit',
    'm_fit',
    'pad',
    'l_pad',
    'm_pad'
] as const;

export type TCropType = (typeof CROP_TYPE)[number];

export type TGravity =
    | 'center'
    | 'auto'
    | 'north'
    | 'south'
    | 'east'
    | 'west'
    | 'face'
    | 'faces'
    | 'custom'
    | 'ocr_text';

export type ICloudinaryTransformation = { [key: string]: any };

export interface IVideoPlaybackProps {
    /** Starts video playback automatically on mount. */
    autoPlay?: boolean;
    /** Loops video playback continuously. */
    loop?: boolean;
    /** Mutes video audio on mount; required for autoPlay in most browsers. */
    muted?: boolean;
    /** Plays video inline on iOS instead of entering fullscreen. */
    playsInline?: boolean;
    /** Shows native browser video controls. */
    controls?: boolean;
}

export interface IMediaDimensions {
    /** Width of the rendered element in pixels or CSS string; also passed to Cloudinary as the transformation width. */
    width?: number | string;
    /** Height of the rendered element in pixels or CSS string; also passed to Cloudinary as the transformation height. */
    height?: number | string;
}

export interface IMediaPosition {
    /** Horizontal crop offset in pixels for Cloudinary transformation. */
    x?: number;
    /** Vertical crop offset in pixels for Cloudinary transformation. */
    y?: number;
}

export interface IMediaElementProps
    extends IMediaDimensions,
        IMediaPosition,
        IVideoPlaybackProps {
    /** DOM id applied to the rendered `<img>` or `<video>` element. */
    mediaElementId?: string;
    /** Cloudinary public ID or full Cloudinary URL; file extension determines rendering mode (image, video, GIF). */
    path: string;
    /** Device pixel ratio for Cloudinary transformations. */
    dpr?: number;
    /** CSS class applied to the root element. */
    className?: string;
    /** Alt text for the rendered image or video; falls back to the Cloudinary path if omitted. */
    alt?: string;
    /** Cloudinary crop mode applied as a transformation (e.g., 'fill', 'thumb', 'fit'). */
    crop?: TCropType;
    /** Cloudinary border radius; use 'max' for a circle. */
    radius?: number | 'max';
    /** Cloudinary gravity for crop positioning (e.g., 'face', 'auto', 'center'). */
    gravity?: TGravity;
    /** Override the default Cloudinary fetch format (default: 'auto', or 'auto:video' for GIFs). */
    fetchFormat?: string;
    /** Selectively removes default Cloudinary flags (quality, flags, fetchFormat) from the request. */
    flagsToRemove?: TMediaElementFlags[];
    /** Cloudinary flags string (default: 'lossy'); skipped for SVG and video files. */
    flags?: string;
    /** Cloudinary quality setting (default: 'auto'); skipped for SVG and video files. */
    quality?: string;
    /** Enables Cloudinary's responsive image behavior, which adjusts the delivered image size based on the rendered element width. */
    responsive?: boolean;
    /** Cloudinary zoom factor applied during face-detection crop; values > 1 zoom out, values < 1 zoom in. */
    zoom?: number;
    /** Ref forwarded to the underlying `<img>` or `<video>` element. */
    mediaRef?: React.Ref<HTMLImageElement | HTMLVideoElement>;
    /** Overrides width/height for the rendered element, used when the image has been resized externally. */
    resizeValues?: { width: number; height: number };
    /** Enables native lazy loading via the `loading="lazy"` attribute. */
    lazyLoad?: boolean;
    /** Cloudinary public ID for a video poster image; prepended with the Cloudinary video base URL. */
    poster?: string;
    /** Restarts a GIF animation by re-setting the image src on each toggle. */
    restartGif?: boolean;
    /** Click handler attached to the Cloudinary context wrapper element. */
    onClick?: () => void;
    /** Additional Cloudinary transformations applied after crop. */
    transformations?: ICloudinaryTransformation[];
    /** Preserves the original Cloudinary URL path when it cannot be extracted, used for lightbox display. */
    isFromLightbox?: boolean;
    /** Prevents automatic GIF-to-video conversion; renders as a standard image instead. */
    disableGifOptimization?: boolean;
}
const CLOUD_NAME = 'honeybook';

function useFlags(
    isSVG: boolean,
    isVideo: boolean,
    isGif: boolean,
    fetchFormat: string,
    quality: string,
    flags: string,
    flagsToRemove: TMediaElementFlags[],
    disableGifOptimization: boolean
) {
    const shouldAddFlags = !(isSVG || isVideo);

    return useMemo(() => {
        const determineFetchFormat = () => {
            if (fetchFormat) {
                return fetchFormat;
            }
            if (isGif && !disableGifOptimization) {
                return 'auto:video';
            }
            return 'auto';
        };

        const mediaElementFlags: IMediaElementFlags = shouldAddFlags
            ? {
                  fetchFormat: determineFetchFormat(),
                  quality: quality || 'auto',
                  flags: flags || 'lossy'
              }
            : {};
        flagsToRemove.forEach(flagToRemove => {
            Reflect.deleteProperty(mediaElementFlags, flagToRemove);
        });
        return mediaElementFlags;
    }, [
        fetchFormat,
        flags,
        flagsToRemove,
        isGif,
        quality,
        shouldAddFlags,
        disableGifOptimization
    ]);
}

function useGifRestart(path: string, shouldRestart: boolean) {
    // Changing the image src after the initial render is enough to restart the gif
    const [_path, setPath] = useState('');

    useEffect(() => setPath(path), [path]);

    return shouldRestart ? _path : path;
}

function ConditionalCloudinaryContext({
    children,
    className,
    onClick
}: PropsWithChildren<{ className?: string; onClick?: () => void }>) {
    const isWrappedWithCloudinaryContext = useContext(
        CloudinaryContext.contextType
    );

    if (!!isWrappedWithCloudinaryContext) {
        // Backwards compatibility for places that passed in className and onClick
        if (onClick || className) {
            return (
                <UnstyledButton className={className} onClick={onClick}>
                    {children}
                </UnstyledButton>
            );
        }

        return <>{children}</>;
    }

    return (
        <CloudinaryContext
            secure
            cloudName={CLOUD_NAME}
            className={className}
            onClick={onClick}
        >
            {children}
        </CloudinaryContext>
    );
}

const HBMediaElement = (
    componentProps: PropsWithChildren<IMediaElementProps>
) => {
    const {
        mediaElementId,
        children,
        path,
        className,
        width,
        height,
        x,
        y,
        crop,
        fetchFormat,
        flags,
        quality,
        mediaRef,
        flagsToRemove = [],
        lazyLoad,
        restartGif,
        onClick,
        poster,
        transformations = [],
        alt,
        isFromLightbox,
        gravity,
        disableGifOptimization = false,
        ...props
    } = componentProps;
    const lastIndex = path?.lastIndexOf('.');
    const fileName = path?.slice(0, lastIndex);
    const fileExtension = path?.slice(lastIndex + 1);
    const imageWidth = componentProps.resizeValues?.width || width;
    const imageHeight = componentProps.resizeValues?.height || height;

    const isSVG = fileExtension === 'svg';
    const isGif = fileExtension === 'gif';
    const isVideo = ['mp4', 'webm', 'mov'].includes(fileExtension);
    const MediaComponent = isVideo ? StyledMediaVideo : StyledMediaImage;
    const flagProps = useFlags(
        isSVG,
        isVideo,
        isGif,
        fetchFormat,
        quality,
        flags,
        flagsToRemove,
        disableGifOptimization
    );
    let _path = isVideo ? fileName : path;

    if (isUrl(_path)) {
        if (isFromLightbox) {
            _path = getCloudinaryPathFromCloudinaryUrl(_path) || _path;
        } else {
            _path = getCloudinaryPathFromCloudinaryUrl(_path);
        }
    }

    _path = useGifRestart(_path, restartGif);

    if (isGif && !disableGifOptimization) {
        return (
            <HBGifAsVideo
                publicId={_path}
                width={imageWidth}
                height={imageHeight}
                crop={crop}
                x={x}
                y={y}
                gravity={gravity}
                transformations={transformations}
                className={className}
                alt={alt || _path}
                autoPlay={props.autoPlay}
                loop={props.loop}
                muted={props.muted}
                playsInline={props.playsInline}
                controls={props.controls}
                poster={poster}
                mediaRef={mediaRef as React.Ref<HTMLVideoElement>}
                restartGif={restartGif}
            />
        );
    }

    const _poster = poster
        ? `${CLOUDINARY_VIDEO_BASE_URL}/${poster}`
        : undefined;

    return (
        <ConditionalCloudinaryContext className={className} onClick={onClick}>
            <MediaComponent
                id={mediaElementId}
                publicId={_path}
                width={imageWidth}
                height={imageHeight}
                {...flagProps}
                innerRef={mediaRef}
                loading={lazyLoad ? 'lazy' : undefined}
                poster={_poster}
                {...props}
                alt={alt || _path}
            >
                {crop && (
                    <Transformation
                        crop={crop}
                        width={width}
                        height={height}
                        x={x}
                        y={y}
                        gravity={gravity}
                    />
                )}
                {transformations.map((transformation, index) => (
                    <Transformation key={index} {...transformation} />
                ))}
            </MediaComponent>
            {children}
        </ConditionalCloudinaryContext>
    );
};

export default HBMediaElement;

export const StyledMediaImage = styled(Image)``;

export const StyledMediaVideo = styled(Video)``;
